import React, { useMemo } from 'react';

import { useAccount } from 'wagmi';

import { ErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundary';
import { Checkbox } from '@/components/shadcn/ui/checkbox';
import { Label } from '@/components/shadcn/ui/label';
import { Currency, TOKENS } from '@/constants';
import { useBtcWallet } from '@/networks/btc-like/btc';
import { useDogeWallet } from '@/networks/btc-like/doge';
import { useLtcWallet } from '@/networks/btc-like/ltc';
import { useCoinwebWallet } from '@/networks/coinweb';
import { useSolanaWallet } from '@/networks/svm';
import { useTronWallet } from '@/networks/tron';
import { useHeaderWalletDrawerStore } from '@/shared/stores/useHeaderWalletDrawerStore';
import {
  isBtcCurrency,
  isBtcLikeCurrency,
  isCwebCurrency,
  isDogeCurrency,
  isErc20Currency,
  isEvmNativeCurrency,
  isLtcCurrency,
  isTrc20Currency,
  isTronCurrency,
  isSolanaCurrency,
  isSplCurrency,
} from '@/utils';

import HeaderWalletDrawerBalanceCard from './HeaderWalletDrawerBalanceCard';
import HeaderWalletDrawerBalanceCardBtc from './HeaderWalletDrawerBalanceCardBtc';
import HeaderWalletDrawerBalanceCardCweb from './HeaderWalletDrawerBalanceCardCweb';
import { HeaderWalletDrawerBalanceCardDoge } from './HeaderWalletDrawerBalanceCardDoge';
import HeaderWalletDrawerBalanceCardLtc from './HeaderWalletDrawerBalanceCardLtc';

const HeaderWalletDrawerBalances: React.FC = () => {
  const btcAccount = useBtcWallet();
  const ltcAccount = useLtcWallet();
  const evmAccount = useAccount();
  const tronAccount = useTronWallet();
  const dogeAccount = useDogeWallet();
  const coinwebAccount = useCoinwebWallet();
  const solanaAccount = useSolanaWallet();
  const {
    settings: { shouldHideSmallBalances },
    updateSettings,
  } = useHeaderWalletDrawerStore();

  const btcLikeAccounts = {
    [Currency.BTC]: btcAccount,
    [Currency.LTC]: ltcAccount,
    [Currency.DOGE]: dogeAccount,
  };

  const cards = useMemo(() => {
    return TOKENS.map(({ name }) => name)
      .filter((currency) => {
        if (isEvmNativeCurrency(currency) || isErc20Currency(currency)) {
          return evmAccount?.isConnected;
        } else if (isBtcLikeCurrency(currency)) {
          return btcLikeAccounts[currency]?.isConnected;
        } else if (isTronCurrency(currency) || isTrc20Currency(currency)) {
          return tronAccount?.isConnected;
        } else if (isSolanaCurrency(currency) || isSplCurrency(currency)) {
          return solanaAccount?.isConnected;
        } else if (isCwebCurrency(currency)) {
          return coinwebAccount?.isConnected;
        }
        return false;
      })
      .sort((a, b) => {
        return a.localeCompare(b);
      })
      .sort((a, b) => {
        // Put BTC like currencies to the top
        if (isBtcLikeCurrency(a)) return -1;
        if (isBtcLikeCurrency(b)) return 1;
        return 0;
      })
      .sort((a, b) => {
        // Put native Coinweb to the very top
        if (isCwebCurrency(a)) return -1;
        if (isCwebCurrency(b)) return 1;
        return 0;
      })
      .map((currency) => {
        const card = (() => {
          switch (true) {
            case isBtcCurrency(currency):
              return <HeaderWalletDrawerBalanceCardBtc shouldHideSmallBalances={shouldHideSmallBalances} />;
            case isLtcCurrency(currency):
              return <HeaderWalletDrawerBalanceCardLtc shouldHideSmallBalances={shouldHideSmallBalances} />;
            case isDogeCurrency(currency):
              return <HeaderWalletDrawerBalanceCardDoge shouldHideSmallBalances={shouldHideSmallBalances} />;
            case isCwebCurrency(currency): {
              return <HeaderWalletDrawerBalanceCardCweb shouldHideSmallBalances={shouldHideSmallBalances} />;
            }
            default:
              return (
                <HeaderWalletDrawerBalanceCard currency={currency} shouldHideSmallBalances={shouldHideSmallBalances} />
              );
          }
        })();

        return (
          <ErrorBoundary key={currency.concat('-wallet-drawer-balance')} fallback={<></>}>
            {card}
          </ErrorBoundary>
        );
      });
  }, [
    shouldHideSmallBalances,
    btcAccount?.isConnected,
    dogeAccount?.isConnected,
    ltcAccount?.isConnected,
    coinwebAccount?.isConnected,
    evmAccount?.isConnected,
    tronAccount?.isConnected,
  ]);

  if (
    [
      btcAccount?.isConnected,
      dogeAccount?.isConnected,
      ltcAccount?.isConnected,
      coinwebAccount?.isConnected,
      evmAccount?.isConnected,
      tronAccount?.isConnected,
    ].some(Boolean)
  ) {
    return (
      <div className="flex flex-col justify-start gap-2">
        <div className="text-muted-foreground flex items-center justify-center gap-2">
          <Checkbox
            id="hide-small-balances"
            checked={shouldHideSmallBalances}
            checkedIconClassName="size-3"
            className="border-muted-foreground/60 data-[state=checked]:border-muted-foreground/60
              data-[state=checked]:text-muted-foreground/60 size-3"
            onCheckedChange={(checked: boolean) => {
              updateSettings({ shouldHideSmallBalances: checked === true });
            }}
          />
          <Label
            htmlFor="hide-small-balances"
            className="text-muted-foreground/60 font-roboto cursor-pointer text-[12px] font-normal select-none"
          >
            Hide small balances
          </Label>
        </div>
        {cards}
      </div>
    );
  }

  return null;
};

export default HeaderWalletDrawerBalances;
